import * as React from 'react';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {ButtonGroup} from 'shells/button-group/button-group';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {StatusBarSecondaryTimestamp} from 'shells/control-bar/status-bar-secondary-timestamp';
// @ts-expect-error - imported from .js/.jsx file
import {BrowserIcon} from 'shells/icon/svg/browser-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ReportsIcon} from 'shells/icon/svg/reports-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {Link} from 'shells/link/link';

import type {LandingPageFullFragment} from 'nutshell-graphql-types';

import {PageContainer, PageTitleBar} from '../../layout';
import type {ReportURLParams} from '../../reports/report-types';

import {EmbedPageButton} from '../embed/embed-page-button';
import {getViewPageLink, getBadgeInfo} from '../helpers';
import {SupportTogglePageStatusButton} from '../support-toggle-page-status-button';

import {EngagementTab} from './engagement-tab';
import {PageTab} from './page-tab';

type Props = ContextRouter & {
    urlParams: ReportURLParams<'contacts'>;
    onParamChange: (arg1: any, arg2: boolean) => any;
    page: LandingPageFullFragment | null | undefined;
    isLoading: boolean;
    errorMessage: string | null | undefined;
    onUpdatePage: (name?: string | null | undefined) => any;
};

export function LandingPagePageContent(props: Props) {
    const {page, isLoading, errorMessage, onUpdatePage, ...restProps} = props;
    const selectedTab: string = props.match.params.tab || 'page';

    const tabOptions = [
        {
            id: 'main',
            label: 'Page',
            icon: BrowserIcon,
            href: generatePath(restProps.match.path, {
                pageId: restProps.match.params.pageId,
                tab: 'main',
            }),
            component: (
                <PageTab page={props.page} isLoading={isLoading} errorMessage={errorMessage} />
            ),
        },
        {
            id: 'engagement',
            label: 'Engagement',
            icon: ReportsIcon,
            href: generatePath(props.match.path, {
                pageId: props.match.params.pageId,
                tab: 'engagement',
            }),
            component: <EngagementTab page={props.page} {...restProps} />,
        },
    ];

    return (
        <PageContainer
            title='Landing pages'
            pageTitleBar={
                <PageTitleBar
                    title={page ? page.name : 'Page'}
                    subheaderPrimaryText=''
                    noMaxWidth={true}
                    breadcrumbs={[
                        {
                            to: '/email-marketing/landing-pages',
                            text: 'Landing pages',
                        },
                    ]}
                    editInPlaceProps={{
                        onChange: (newValue: string | null | undefined) =>
                            page ? onUpdatePage(newValue ? newValue : '') : undefined,
                        placeholder: 'Name this page',
                    }}
                    buttons={
                        <ButtonGroup>
                            {NutClientConfig.isInternalLogin && props.page ? (
                                <SupportTogglePageStatusButton page={props.page} />
                            ) : undefined}
                            {NutClientConfig.allowEmbeddedLandingPages && props.page && (
                                <EmbedPageButton page={props.page} />
                            )}
                            {props.page ? (
                                <Link
                                    variant='button-primary'
                                    size='big'
                                    newTab={true}
                                    href={getViewPageLink(props.page)}
                                >
                                    View page
                                </Link>
                            ) : undefined}
                        </ButtonGroup>
                    }
                />
            }
            controlBar={
                <PageControlBar
                    statusInfo={{
                        isLoading: !props.page && props.isLoading,
                        // @ts-expect-error
                        status: getBadgeInfo(props.page && props.page.status),
                        primaryText: props.page
                            ? `${props.page.numViews} ${
                                  props.page.numViews === 1 ? 'view' : 'views'
                              }`
                            : '0 views',
                        secondaryText: (
                            <StatusBarSecondaryTimestamp
                                datetime={props.page && props.page.lastModifiedTime}
                                prefix='Last updated'
                                emptyText=''
                            />
                        ),
                    }}
                    tabs={{selectedTab, options: tabOptions}}
                />
            }
        >
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={selectedTab} tabOptions={tabOptions} />
        </PageContainer>
    );
}
